import { useMemo } from 'react';

import { ProductColor } from '@ravago/shared/page-data/models/elements';
import { LeadTimeIndicator } from '@ravago/shared/page-data/models/elements/product-lead-time.model';
import { Badge } from '@ravago/shared/radiance/components/badge/components/Badge/Badge';
import { Box } from '@ravago/shared/radiance/components/box/components/Box/Box';
import { ColorIcon } from '@ravago/shared/radiance/components/icon/components/ColorIcon/ColorIcon';
import { RadioCard } from '@ravago/shared/radiance/components/radio/components/RadioCard/RadioCard';
import { RadioCardGroup } from '@ravago/shared/radiance/components/radio/components/RadioCardGroup/RadioCardGroup';
import { Typography } from '@ravago/shared/radiance/components/typography/components/Typography/Typography';
import { useTranslations } from '@ravago/shared/translations/client';

import { hasMtsLocalLeadTime, LeadTimeCheckCriteria } from '../../../../../utils/lead-time';
import { BLANK_COLOR_ID, OTHER_COLOR_ID, UNKNOWN_COLOR_ID } from '../../constants';
import { usePathCountryCode } from '@ravago/shared/page-elements-radiance/hooks/use-path-locale';

interface Props {
   colors: ProductColor[];
   leadTimeIndicators: LeadTimeIndicator[];
   leadTimeEnabled: boolean;
   value: number | undefined;
   onChange: (value: number) => void;
   hideExtraColorOptions?: boolean;
}

interface RfqColorRadioProps {
   colorName?: string;
   label: string;
   isMtsLocal: boolean;
   showLeadTimeIndicator: boolean;
}

const RfqColorRadio = ({ colorName, label, isMtsLocal, showLeadTimeIndicator }: RfqColorRadioProps) => {
   const translate = useTranslations();

   return (
      <Box gap="md" justify="between" items="center">
         <Box gap="xs" items="center">
            <ColorIcon colorName={colorName} />
            <Typography>{label}</Typography>{' '}
         </Box>

         {showLeadTimeIndicator && (
            <Badge leadingIcon="truck" variant={isMtsLocal ? 'info' : 'neutral'}>
               {isMtsLocal
                  ? translate('request-for-quotation.lead-time.mts-local-days')
                  : translate('request-for-quotation.lead-time.delivery-on-request')}
            </Badge>
         )}
      </Box>
   );
};

export const RfqColor = ({ colors, leadTimeIndicators, leadTimeEnabled, value, onChange, hideExtraColorOptions = false }: Props) => {
   const translate = useTranslations();
   const countryCode = usePathCountryCode();

   const colorMtsLocalMap = useMemo(() => {
      if (!leadTimeEnabled) return {};

      return colors.reduce((acc, { id, producerColorCodes }) => {
         const criteria: LeadTimeCheckCriteria = {
            colorId: id,
            countryCode,
         };
         const producerColorCodeCriteriaList: LeadTimeCheckCriteria[] = producerColorCodes.map(({ colorCode }) => ({
            colorId: id,
            producerColorCode: colorCode,
            countryCode,
         }));
         acc[id] =
            hasMtsLocalLeadTime(leadTimeIndicators, criteria) ||
            producerColorCodeCriteriaList.some((producerColorCodeCriteria) => hasMtsLocalLeadTime(leadTimeIndicators, producerColorCodeCriteria));
         return acc;
      }, {} as Record<string, boolean>);
   }, [colors, countryCode, leadTimeIndicators, leadTimeEnabled]);
   const hasMtsLocal = Object.values(colorMtsLocalMap).some((colorCodeMtsLocal) => colorCodeMtsLocal);

   const sortedColorOptions = useMemo(() => {
      const options = colors.map((color) => ({
         id: color.id.toString(),
         label: translate(`colors.${color.name.toLowerCase().replace(' ', '-')}`),
         colorName: color.name.toLowerCase()
      }));

      if (!leadTimeEnabled) return options;

      const mtsLocalColors = options.filter(({ id }) => colorMtsLocalMap[id]);
      const nonMtsLocalColors = options.filter(({ id }) => !colorMtsLocalMap[id]);

      return [...mtsLocalColors, ...nonMtsLocalColors];
   }, [colors, leadTimeEnabled, colorMtsLocalMap]);

   const hasOtherOption = colors.some((color) => color.id === OTHER_COLOR_ID || color.id === BLANK_COLOR_ID);

   const inputValue = value ? value.toString() : '';

   const handleChange = (newValue: string) => {
      onChange(Number(newValue));
   };

   return (
      <RadioCardGroup value={inputValue} onChange={handleChange}>
         {sortedColorOptions.map((option) => (
            <RadioCard key={option.id} value={option.id}>
               <RfqColorRadio
                  label={option.label}
                  colorName={option.colorName}
                  isMtsLocal={colorMtsLocalMap[option.id]}
                  showLeadTimeIndicator={hasMtsLocal}
               />
            </RadioCard>
         ))}

         {!hideExtraColorOptions && !hasOtherOption && (
            <RadioCard value={OTHER_COLOR_ID.toString()}>
               <RfqColorRadio label={translate('colors.other')} isMtsLocal={colorMtsLocalMap[OTHER_COLOR_ID]} showLeadTimeIndicator={hasMtsLocal} />
            </RadioCard>
         )}

         {!hideExtraColorOptions && (
            <RadioCard value={UNKNOWN_COLOR_ID.toString()}>
               <RfqColorRadio label={translate('colors.unknown')} isMtsLocal={false} showLeadTimeIndicator={false} />
            </RadioCard>
         )}
      </RadioCardGroup>
   );
};
